import { get, writable } from 'svelte/store';

/**
 * Create an access-check API bound to a specific app's stores and data layer.
 *
 * Consumers (Education, TAP) instantiate this once and export the returned
 * functions for use by pages/components. The returned API manages three
 * internal arrays — `userRoles`, `userActions`, `userContentActions` —
 * populated by `setRoleContext` (from role docs) and `rebuildSubscriptions`
 * (from subscription content actions).
 *
 * @param {object} params
 * @param {import('svelte/store').Writable<any>} params.currentUser
 * @param {import('svelte/store').Writable<any>} params.userSubscriptionsStore
 * @param {import('svelte/store').Writable<any>} params.userContentActionsStore
 * @param {(ids: string[], collection: string) => Promise<any[]>} params.getEntities
 * @param {(data: any, collection: string) => Promise<any>} params.getEntitiesWhere
 * @param {() => Promise<any>} [params.getUserSubscriptions] - Fallback loader used by
 *        canAccessResource when the store is empty.
 * @param {(subscription: any) => boolean} params.isSubscriptionExpired
 * @param {object} params.enums - `{ roles, actions, contentActions, actionRequire, roleRequire }`
 * @param {object} params.collectionTypes - At minimum: `{ COURSES, CONTENT_ACTIONS, COLLECTIONS }`
 */
export function createAccessApi({
    currentUser,
    userSubscriptionsStore,
    userContentActionsStore,
    getEntities,
    getEntitiesWhere,
    getUserSubscriptions,
    isSubscriptionExpired,
    enums,
    collectionTypes,
}) {
    if (!currentUser)
        throw new Error('createAccessApi: currentUser store required');
    if (!userSubscriptionsStore)
        throw new Error('createAccessApi: userSubscriptionsStore required');
    if (!userContentActionsStore)
        throw new Error('createAccessApi: userContentActionsStore required');
    if (!enums?.actionRequire || !enums?.roleRequire)
        throw new Error(
            'createAccessApi: enums.actionRequire/roleRequire required'
        );

    const userActions = [];
    const userRoles = [];
    let userContentActions = [{ id: 'role', materials: [], actions: [] }];
    const resourceAccessCache = {};

    // Reactive gate: flips true once role-derived access state has been
    // populated (or explicitly marked ready for apps without role docs).
    // Consumers that need to run hasRole/hasAction/hasContentAction after
    // auth settles should subscribe to this instead of racing the async load.
    const rolesReady = writable(false);

    // Display-only mirror of `userContentActions` for expired/revoked
    // subscriptions: same `{ id, materials, courses, name, endDate }` shape,
    // resolved from the same course docs, but never merged into
    // `userContentActions` — that store is the authorization source and must
    // stay active-only. UIs that need to *show* a lapsed grant (TAP's expired
    // shelves and its redeemed-codes table) union the two themselves.
    const expiredContentActions = writable([]);

    const {
        actionRequire,
        roleRequire,
        contentActions: contentActionsEnum,
    } = enums;

    function reset() {
        userActions.splice(0, userActions.length);
        userRoles.splice(0, userRoles.length);
        userContentActions = [{ id: 'role', materials: [], actions: [] }];
        userContentActionsStore.set([]);
        expiredContentActions.set([]);
        userSubscriptionsStore.set({});
        for (const k of Object.keys(resourceAccessCache))
            delete resourceAccessCache[k];
        rolesReady.set(false);
    }

    /**
     * Replace the role-derived slice of the access state. Called after fetching the
     * signed-in user's role docs and resolving their actions + content actions.
     */
    function setRoleContext({
        nextRoles = [],
        nextActions = [],
        nextContentActions = [],
    } = {}) {
        userActions.splice(0, userActions.length, ...nextActions);
        userRoles.splice(0, userRoles.length, ...nextRoles);
        userContentActions = [
            { id: 'role', materials: [], actions: nextContentActions },
        ];
        rolesReady.set(true);
    }

    /**
     * For apps whose users have no role docs (e.g. TAP) — mark roles as ready
     * once subscription hydration has finished, so role-gated effects can
     * unblock.
     */
    function markRolesReady() {
        rolesReady.set(true);
    }

    /**
     * Given the bucketed subscriptions `{administrator, seatholder, teacher, student}`,
     * fetch each subscription's courses + content-action definitions, then rebuild
     * the per-subscription content-action entries. Also writes the buckets to
     * `userSubscriptionsStore` and the processed entries to `userContentActionsStore`.
     */
    async function rebuildSubscriptions(subscriptions) {
        // Subscription state changed — cached canAccessResource answers (and
        // the cached fallback subscription fetch) may no longer hold.
        invalidateResourceAccessCache();
        userSubscriptionsStore.set(subscriptions);
        // Drop resource verdicts decided against the previous subscription set.
        invalidateResourceAccessCache();
        const allSubscriptions = [
            ...(subscriptions?.seatholder || []), // TODO: BZW-240
            ...(subscriptions?.teacher || []),
            ...(subscriptions?.student || []),
            ...(subscriptions?.administrator || []),
        ];

        // Expired/revoked (active !== true) subscriptions stay in the store —
        // UIs may still display them — but must not grant materials or
        // content actions.
        const combined = allSubscriptions.filter(
            (sub) => !isSubscriptionExpired(sub)
        );
        const inactive = allSubscriptions.filter((sub) =>
            isSubscriptionExpired(sub)
        );
        const inactiveIds = new Set(inactive.map((sub) => sub.id));

        const courseIds = new Set();
        const contentActionIds = new Set();
        // Courses are resolved for the expired set too, so `expiredContentActions`
        // can name the books a lapsed subscription used to unlock. Content
        // actions are not — an expired entry never carries any.
        allSubscriptions.forEach((sub) => {
            (sub?.courses || []).forEach((c) => courseIds.add(c?.id || c));
        });
        combined.forEach((sub) => {
            (sub?.contentActions || []).forEach((a) => contentActionIds.add(a));
        });

        const [courseEntities, contentActionEntities] = await Promise.all([
            courseIds.size
                ? getEntities([...courseIds], collectionTypes.COURSES).catch(
                      console.error
                  )
                : [],
            contentActionIds.size
                ? getEntities(
                      [...contentActionIds],
                      collectionTypes.CONTENT_ACTIONS
                  ).catch(console.error)
                : [],
        ]);

        const courseMap = new Map();
        courseEntities?.forEach((d) => {
            if (d?.id) courseMap.set(d.id, d.data());
        });
        const contentActionMap = new Map();
        contentActionEntities?.forEach((d) => {
            if (d?.id) contentActionMap.set(d.id, d.data());
        });

        // `id`/`materials`/`actions` are load-bearing across apps — the
        // extra fields (name/endDate/courses) exist so TAP can group its
        // shelves by course and show expiry chips. `withActions` is false for
        // the expired projection: those entries are for display only and must
        // never carry a content action.
        const toEntry = (subs, { withActions = true } = {}) => {
            const subobj = {
                id: subs.id,
                materials: [],
                actions: [],
                name: subs.name || null,
                endDate: subs.endDate ?? null,
                courses: [],
            };
            (subs.courses || []).forEach((c) => {
                const courseId = c?.id || c;
                const data = courseMap.get(courseId);
                if (data?.materials?.length) {
                    subobj.materials = [...subobj.materials, ...data.materials];
                    subobj.courses.push({
                        id: courseId,
                        title: data.name || data.title || null,
                        materials: [...data.materials],
                    });
                }
            });
            if (withActions) {
                (subs.contentActions || []).forEach((actionId) => {
                    const data = contentActionMap.get(actionId);
                    const key = data?.action?.split('.')[1];
                    if (key && contentActionsEnum[key])
                        subobj.actions.push(contentActionsEnum[key]);
                });
            }
            return subobj;
        };

        const processed = combined.map((subs) => toEntry(subs));
        expiredContentActions.set(
            inactive.map((subs) => toEntry(subs, { withActions: false }))
        );

        // Drop entries for subscriptions revoked/expired since the last
        // rebuild — the merge below only updates or appends.
        const localUserContentActions = [...userContentActions].filter(
            (entry) => !inactiveIds.has(entry.id)
        );
        processed.forEach((subobj) => {
            const idx = localUserContentActions.findIndex(
                (e) => e.id === subobj.id
            );
            if (idx > -1) localUserContentActions[idx] = subobj;
            else localUserContentActions.push(subobj);
        });

        userContentActions = [...localUserContentActions];
        userContentActionsStore.set(localUserContentActions);
    }

    function hasAction(action, require) {
        require = require || actionRequire.ANY;
        if (!get(currentUser)) return false;
        if (!action.filter) action = [action];
        let allow;
        switch (require) {
            case actionRequire.ALL:
                allow = true;
                action.forEach((a) => {
                    if (userActions.indexOf(a) < 0) allow = false;
                });
                break;
            case actionRequire.ANY:
                allow = false;
                action.forEach((a) => {
                    if (userActions.indexOf(a) > -1) allow = true;
                });
                break;
        }
        return allow;
    }

    function hasContentAction(action, material, require) {
        require = require || actionRequire.ANY;
        if (!get(currentUser) || !action || !material) return false;
        if (!action.filter) action = [action];
        let allow;
        switch (require) {
            case actionRequire.ALL: {
                const ta = [].concat(action);
                for (const a in ta) {
                    for (const ca in userContentActions) {
                        if (
                            userContentActions[ca].materials.indexOf(material) >
                                -1 &&
                            userContentActions[ca].actions.indexOf(ta[a]) > -1
                        )
                            ta.splice(action[a], 1);
                    }
                }
                allow = ta.length === 0;
                break;
            }
            case actionRequire.ANY:
                allow = false;
                for (const a in action) {
                    for (const ca in userContentActions) {
                        if (
                            userContentActions[ca].materials.indexOf(material) >
                                -1 &&
                            userContentActions[ca].actions.indexOf(action[a]) >
                                -1
                        )
                            allow = true;
                    }
                }
                break;
        }
        return allow;
    }

    function hasRole(role, require) {
        require = require || roleRequire.ANY;
        if (!get(currentUser)) return false;
        if (!role.filter) role = [role];
        let allow;
        switch (require) {
            case roleRequire.ALL:
                allow = true;
                role.forEach((r) => {
                    if (userRoles.indexOf(r) < 0) allow = false;
                });
                break;
            case roleRequire.ANY:
                allow = false;
                role.forEach((r) => {
                    if (userRoles.indexOf(r) > -1) allow = true;
                });
                break;
        }
        return allow;
    }

    async function canAccessResource(
        resourceId,
        returnSubscription = false,
        returnFinalCourse = false
    ) {
        const cacheKey = `${resourceId}-${returnSubscription}-${returnFinalCourse}`;
        if (cacheKey in resourceAccessCache && !returnSubscription) {
            return resourceAccessCache[cacheKey];
        }

        let courseId = null;
        const visited = new Set();
        const collectionLookupCache = new Map();
        const courseLookupCache = new Map();

        async function fetchCollections(id) {
            if (collectionLookupCache.has(id))
                return collectionLookupCache.get(id);
            const collections = await getEntitiesWhere(
                {
                    needles: [
                        {
                            field: 'items',
                            operator: 'array-contains',
                            match: id,
                        },
                    ],
                },
                collectionTypes.COLLECTIONS
            );
            const data =
                collections?.docs?.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) || [];
            collectionLookupCache.set(id, data);
            return data;
        }

        async function fetchCourses(id) {
            if (courseLookupCache.has(id)) return courseLookupCache.get(id);
            const courses = await getEntitiesWhere(
                {
                    needles: [
                        {
                            field: 'materials',
                            operator: 'array-contains',
                            match: id,
                        },
                    ],
                },
                collectionTypes.COURSES
            );
            const data =
                courses?.docs?.map((doc) => ({ id: doc.id, ...doc.data() })) ||
                [];
            courseLookupCache.set(id, data);
            return data;
        }

        async function findSubscriptions(targetId, subscriptions) {
            const foundSubscriptions = [];

            async function searchRecursively(id) {
                if (visited.has(id)) return;
                visited.add(id);

                const [collectionData, courseData] = await Promise.all([
                    fetchCollections(id),
                    fetchCourses(id),
                ]);

                for (const subscription of subscriptions) {
                    for (const collection of collectionData) {
                        if (
                            (subscription.courses.includes(collection.id) ||
                                subscription.courses.findIndex(
                                    (c) => (c?.id || c) === collection?.id
                                ) > -1) &&
                            !foundSubscriptions.includes(subscription)
                        ) {
                            foundSubscriptions.push(subscription);
                            courseId = collection.id;
                        }
                    }
                    for (const course of courseData) {
                        if (
                            (subscription.courses.includes(course.id) ||
                                subscription.courses.findIndex(
                                    (c) => (c?.id || c) === course?.id
                                ) > -1) &&
                            !foundSubscriptions.includes(subscription)
                        ) {
                            foundSubscriptions.push(subscription);
                            courseId = course.id;
                        }
                    }
                }

                await Promise.all([
                    ...collectionData.map((c) => searchRecursively(c.id)),
                    ...courseData.map((c) => searchRecursively(c.id)),
                ]);
            }

            for (const subscription of subscriptions) {
                if (subscription.courses.includes(targetId)) {
                    foundSubscriptions.push(subscription);
                    courseId = targetId;
                }
            }

            await searchRecursively(targetId);
            return foundSubscriptions;
        }

        let subscriptions = Object.values(get(userSubscriptionsStore)).flat();
        if (
            subscriptions.length === 0 &&
            typeof getUserSubscriptions === 'function'
        ) {
            if (!resourceAccessCache.userSubscriptions) {
                const userSubs = await getUserSubscriptions();
                resourceAccessCache.userSubscriptions =
                    Object.values(userSubs).flat();
            }
            subscriptions = resourceAccessCache.userSubscriptions;
        }

        if (subscriptions.length === 0) {
            console.warn('No subscriptions');
            return false;
        }

        const foundSubscriptions = await findSubscriptions(
            resourceId,
            subscriptions
        );

        if (returnSubscription) {
            if (foundSubscriptions.length === 0) {
                console.warn('No subscriptions found');
                return [];
            }
            foundSubscriptions.sort(
                (a, b) =>
                    new Date(b.endDate).getTime() -
                    new Date(a.endDate).getTime()
            );
            const result = returnFinalCourse
                ? { subscription: foundSubscriptions[0], courseId }
                : foundSubscriptions[0];
            resourceAccessCache[cacheKey] = result;
            return result;
        }

        if (returnFinalCourse) {
            resourceAccessCache[cacheKey] = courseId;
            return courseId;
        }

        if (foundSubscriptions.length === 0) {
            console.warn('No subscriptions found');
            return false;
        }

        for (const subscription of foundSubscriptions) {
            if (!isSubscriptionExpired(subscription)) {
                resourceAccessCache[cacheKey] = true;
                return true;
            }
        }

        resourceAccessCache[cacheKey] = false;
        return false;
    }

    function invalidateResourceAccessCache() {
        for (const k of Object.keys(resourceAccessCache))
            delete resourceAccessCache[k];
    }

    return {
        // Checks
        hasAction,
        hasContentAction,
        hasRole,
        canAccessResource,
        // Mutators
        setRoleContext,
        rebuildSubscriptions,
        reset,
        invalidateResourceAccessCache,
        markRolesReady,
        // Reactive signals
        rolesReady,
        expiredContentActions,
        // Read-only peeks (rarely needed; expose for adapters that reconstruct old patterns)
        peek: {
            userRoles: () => [...userRoles],
            userActions: () => [...userActions],
            userContentActions: () => userContentActions,
        },
    };
}
